import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export interface ProductOption {
  id: string;
  name: string;
  base_price?: number | null;
}

interface MultiSelectProductsProps {
  products: ProductOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

export function MultiSelectProducts({ products, value, onChange, placeholder = "Select products" }: MultiSelectProductsProps) {
  const label = useMemo(() => {
    if (!value?.length) return placeholder;
    if (value.length === 1) return `${products.find(p => p.id === value[0])?.name ?? "1 selected"}`;
    return `${value.length} selected`;
  }, [value, products, placeholder]);

  const toggle = (id: string) => {
    if (value.includes(id)) onChange(value.filter(v => v !== id));
    else onChange([...(value || []), id]);
  };

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("w-full justify-between")}>{label}</Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 z-50" align="start">
        <Command>
          <CommandInput placeholder="Search products..." />
          <CommandList>
            <CommandEmpty>No products found.</CommandEmpty>
            <CommandGroup>
              {products.map((p) => (
                <CommandItem key={p.id} onSelect={() => toggle(p.id)} className="cursor-pointer">
                  <Checkbox checked={value.includes(p.id)} className="mr-2" />
                  <span className="flex-1">{p.name}</span>
                  {p.base_price != null && (
                    <span className="text-muted-foreground text-xs">{Number(p.base_price).toFixed(2)}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
